import React, { useState, useEffect } from "react";
import { ArrowRight, CheckCircle } from "lucide-react";

const Tag = ({ children }) => (
  <span className="bg-orange-100 text-orange-700 px-1 rounded font-mono font-semibold text-[10px] border border-orange-200">
    {children}
  </span>
);

const Value = ({ children }) => (
  <span className="text-slate-800 font-semibold">{children}</span>
);

const WordTemplate = () => (
  <div className="bg-white rounded-xl border-2 border-blue-300 shadow-md overflow-hidden">
    {/* Word title bar */}
    <div className="bg-blue-600 px-3 py-2 flex items-center gap-2">
      <div className="w-4 h-4 rounded bg-white flex items-center justify-center">
        <span className="text-blue-600 font-bold text-[9px]">W</span>
      </div>
      <span className="text-white text-xs font-medium">invoice_template.docx</span>
    </div>
    {/* Formatting toolbar */}
    <div className="bg-blue-50 border-b border-blue-100 px-3 py-1 flex gap-1.5 items-center">
      {["B", "I", "U"].map(f => (
        <span key={f} className="text-blue-400 text-[10px] font-bold border border-blue-200 rounded px-1">{f}</span>
      ))}
      <span className="text-blue-300 text-[10px] ml-1">| Calibri · 11pt</span>
    </div>
    {/* Document content */}
    <div className="p-4 text-xs space-y-2.5 font-mono bg-white">
      <div className="text-center font-bold text-base text-slate-800 tracking-wide border-b border-slate-200 pb-2">INVOICE</div>

      <div className="flex justify-between text-[10px]">
        <div>
          <div className="font-bold text-slate-700">Acme Corp</div>
          <div className="text-slate-500">123 Market St, San Francisco, CA</div>
        </div>
        <div className="text-right space-y-0.5">
          <div>Invoice #: <Tag>{"{{Invoice.Number}}"}</Tag></div>
          <div>Date: <Tag>{"{{Invoice.Date}}"}</Tag></div>
          <div>Due: <Tag>{"{{Invoice.DueDate}}"}</Tag></div>
        </div>
      </div>

      <div className="text-[10px] border border-slate-200 rounded p-2 bg-slate-50">
        <span className="font-bold text-slate-600">Bill To: </span>
        <Tag>{"{{Contact.Name}}"}</Tag>
        <span className="text-slate-400">, </span>
        <Tag>{"{{Account.Name}}"}</Tag>
        <div className="mt-1 text-slate-500"><Tag>{"{{Contact.Email}}"}</Tag></div>
      </div>

      {/* Line items table */}
      <table className="w-full border-collapse text-[10px] table-fixed">
        <colgroup>
          <col style={{ width: "48%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "32%" }} />
        </colgroup>
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="px-2 py-1 text-left font-semibold">Description</th>
            <th className="px-2 py-1 text-center font-semibold">Qty</th>
            <th className="px-2 py-1 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-200 bg-white">
            <td className="px-1 py-1 overflow-hidden"><Tag>{"{{Item1.Name}}"}</Tag></td>
            <td className="px-1 py-1 overflow-hidden"><div className="flex justify-center"><Tag>{"{{Item1.Qty}}"}</Tag></div></td>
            <td className="px-1 py-1 overflow-hidden"><div className="flex justify-end"><Tag>{"{{Item1.Amt}}"}</Tag></div></td>
          </tr>
          <tr className="border-b border-slate-200 bg-slate-50">
            <td className="px-1 py-1 overflow-hidden"><Tag>{"{{Item2.Name}}"}</Tag></td>
            <td className="px-1 py-1 overflow-hidden"><div className="flex justify-center"><Tag>{"{{Item2.Qty}}"}</Tag></div></td>
            <td className="px-1 py-1 overflow-hidden"><div className="flex justify-end"><Tag>{"{{Item2.Amt}}"}</Tag></div></td>
          </tr>
        </tbody>
      </table>

      <div className="text-right text-[10px] space-y-0.5">
        <div className="text-slate-500">Subtotal: <Tag>{"{{Invoice.Subtotal}}"}</Tag></div>
        <div className="text-slate-500">Tax (9%): <Tag>{"{{Invoice.Tax}}"}</Tag></div>
        <div className="font-bold text-slate-800 text-[11px]">Total Due: <Tag>{"{{Invoice.Total}}"}</Tag></div>
      </div>
    </div>
  </div>
);

const PdfOutput = () => (
  <div className="bg-white rounded-xl border-2 border-red-300 shadow-md overflow-hidden">
    {/* PDF title bar */}
    <div className="bg-red-600 px-3 py-2 flex items-center gap-2">
      <div className="w-5 h-5 rounded bg-white flex items-center justify-center flex-shrink-0">
        <span className="text-red-600 font-black text-[7px] leading-none tracking-tight">PDF</span>
      </div>
      <span className="text-white text-xs font-medium">invoice_INV-2025-0042.pdf</span>
      <span className="ml-auto text-red-200 text-[10px]">🔒 Read-only</span>
    </div>
    {/* PDF toolbar */}
    <div className="bg-red-50 border-b border-red-100 px-3 py-1 flex items-center">
      <span className="text-red-300 text-[10px]">Page 1 of 1</span>
      <span className="ml-auto text-red-400 text-[10px]">100%</span>
    </div>
    {/* PDF content */}
    <div className="p-4 text-xs space-y-2.5 font-mono bg-gray-50">
      <div className="text-center font-bold text-base text-slate-800 tracking-wide border-b border-slate-300 pb-2">INVOICE</div>

      <div className="flex justify-between text-[10px]">
        <div>
          <div className="font-bold text-slate-700">Acme Corp</div>
          <div className="text-slate-500">123 Market St, San Francisco, CA</div>
        </div>
        <div className="text-right space-y-0.5">
          <div className="text-slate-600">Invoice #: <Value>INV-2025-0042</Value></div>
          <div className="text-slate-600">Date: <Value>Jan 15, 2025</Value></div>
          <div className="text-slate-600">Due: <Value>Feb 15, 2025</Value></div>
        </div>
      </div>

      <div className="text-[10px] border border-slate-200 rounded p-2 bg-white">
        <span className="font-bold text-slate-600">Bill To: </span>
        <Value>John Smith</Value>
        <span className="text-slate-400">, </span>
        <Value>Acme Enterprises</Value>
        <div className="mt-1 text-slate-500"><Value>john.smith@acme.com</Value></div>
      </div>

      {/* Line items table */}
      <table className="w-full border-collapse text-[10px] table-fixed">
        <colgroup>
          <col style={{ width: "48%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "32%" }} />
        </colgroup>
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="px-2 py-1 text-left font-semibold">Description</th>
            <th className="px-2 py-1 text-center font-semibold">Qty</th>
            <th className="px-2 py-1 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-200 bg-white">
            <td className="px-2 py-1 text-slate-700 overflow-hidden">Enterprise License</td>
            <td className="px-2 py-1 text-center text-slate-700">5</td>
            <td className="px-2 py-1 text-right font-medium text-slate-800">$7,500.00</td>
          </tr>
          <tr className="border-b border-slate-200 bg-slate-50">
            <td className="px-2 py-1 text-slate-700 overflow-hidden">Implementation Hrs</td>
            <td className="px-2 py-1 text-center text-slate-700">10</td>
            <td className="px-2 py-1 text-right font-medium text-slate-800">$2,000.00</td>
          </tr>
        </tbody>
      </table>

      <div className="text-right text-[10px] space-y-0.5">
        <div className="text-slate-500">Subtotal: <Value>$9,500.00</Value></div>
        <div className="text-slate-500">Tax (9%): <Value>$855.00</Value></div>
        <div className="font-bold text-slate-800 text-[11px]">Total Due: <Value>$10,355.00</Value></div>
      </div>
    </div>
  </div>
);

const processingSteps = [
  "Connecting to Salesforce...",
  "Fetching Opportunity & Contact records...",
  "Merging fields into Word template...",
  "Converting .docx → .pdf...",
];

const ProductDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);

  useEffect(() => {
    const duration = currentStep === 1 ? 2000 : 4500;
    const timer = setTimeout(() => {
      setCurrentStep((prev) => (prev + 1) % 3);
    }, duration);
    return () => clearTimeout(timer);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 1) {
      setProcessingProgress(0);
      [200, 500, 900, 1400].forEach((delay, i) => {
        setTimeout(() => setProcessingProgress(i + 1), delay);
      });
    }
  }, [currentStep]);

  const demoSteps = [
    { label: "Word Template", shortLabel: "Word" },
    { label: "Processing",    shortLabel: "Processing" },
    { label: "PDF Generated", shortLabel: "PDF" },
  ];

  return (
    <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 shadow-2xl p-4 sm:p-8">
      {/* Step indicators */}
      <div className="flex justify-center items-center mb-6 sm:mb-8 gap-1 sm:gap-2">
        {demoSteps.map((step, index) => (
          <div key={index} className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setCurrentStep(index)} className="flex items-center gap-1 sm:gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${
                index === currentStep
                  ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white scale-110 shadow-md"
                  : index < currentStep
                  ? "bg-orange-300 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}>
                {index < currentStep ? "✓" : index + 1}
              </div>
              <span className={`hidden sm:inline text-xs font-medium transition-colors duration-300 ${
                index === currentStep ? "text-orange-600" : "text-slate-400"
              }`}>{step.label}</span>
              <span className={`sm:hidden text-[10px] font-medium transition-colors duration-300 ${
                index === currentStep ? "text-orange-600" : "text-slate-400"
              }`}>{step.shortLabel}</span>
            </button>
            {index < demoSteps.length - 1 && (
              <div className={`w-6 sm:w-10 h-0.5 transition-colors duration-500 ${index < currentStep ? "bg-orange-300" : "bg-slate-200"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Demo Content — desktop: 3-col side by side; mobile: show active panel only */}

      {/* Mobile: single active panel */}
      <div className="block lg:hidden">
        {currentStep === 0 && <WordTemplate />}
        {currentStep === 1 && (
          <div className="flex justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500 scale-110 animate-pulse shadow-xl">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="text-[11px] font-semibold px-3 py-1 rounded-full bg-orange-100 text-orange-600">converting...</div>
            </div>
          </div>
        )}
        {currentStep === 2 && <PdfOutput />}
      </div>

      {/* Desktop: 3-col grid */}
      <div className="hidden lg:grid grid-cols-3 gap-6 items-center min-h-96">

        {/* Left — Word Template */}
        <div className={`transition-all duration-500 ${currentStep === 2 ? "opacity-50 scale-95" : "opacity-100 scale-100"}`}>
          <WordTemplate />
        </div>

        {/* Center — Conversion Arrow */}
        <div className="flex justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
              currentStep === 1
                ? "bg-gradient-to-r from-orange-500 to-pink-500 scale-110 animate-pulse shadow-xl"
                : currentStep === 2
                ? "bg-gradient-to-r from-green-400 to-emerald-500 shadow-md"
                : "bg-gradient-to-r from-orange-400 to-pink-400"
            }`}>
              {currentStep === 1 ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : currentStep === 2 ? (
                <CheckCircle className="text-white animate-bounce" size={26} />
              ) : (
                <ArrowRight className="text-white" size={26} />
              )}
            </div>
            <div className={`text-[11px] font-semibold px-3 py-1 rounded-full text-center ${
              currentStep === 0 ? "bg-slate-100 text-slate-500" :
              currentStep === 1 ? "bg-orange-100 text-orange-600" :
              "bg-green-100 text-green-600"
            }`}>
              {currentStep === 0 ? ".docx → .pdf" : currentStep === 1 ? "converting..." : "done!"}
            </div>
          </div>
        </div>

        {/* Right — PDF Output */}
        <div className={`transition-all duration-700 ${
          currentStep === 0 ? "opacity-20 scale-95 blur-sm" :
          currentStep === 1 ? "opacity-40 scale-95" :
          "opacity-100 scale-100"
        }`}>
          <PdfOutput />
        </div>
      </div>

      {/* Processing steps — visible only during step 1 */}
      {currentStep === 1 && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Conversion Progress</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {processingSteps.map((label, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs transition-all duration-500 ${i < processingProgress ? "opacity-100" : "opacity-25"}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold transition-colors duration-300 ${
                  i < processingProgress ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"
                }`}>
                  {i < processingProgress ? "✓" : i + 1}
                </div>
                <span className={i < processingProgress ? "text-slate-700" : "text-slate-400"}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success state — step 2 */}
      {currentStep === 2 && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex flex-col items-center text-center gap-1">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
              <p className="text-sm font-semibold text-green-800">PDF generated successfully!</p>
            </div>
            <p className="text-xs text-green-600">
              Salesforce data merged · Tables preserved · Formatting intact · Ready to send
            </p>
          </div>
        </div>
      )}

      {/* Step 0 hint */}
      {currentStep === 0 && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center">
            <span className="font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[11px]">{"{{field tags}}"}</span>
            {" "}in your Word template are automatically replaced with live Salesforce data and exported as a pixel-perfect PDF.
          </p>
        </div>
      )}

      {/* Demo controls */}
      <div className="text-center mt-6">
        <div className="inline-flex items-center space-x-2">
          {demoSteps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 hover:scale-125 ${
                index === currentStep
                  ? "bg-gradient-to-r from-orange-500 to-pink-500 scale-125"
                  : "bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">Click dots to navigate · Auto-plays on loop</p>
      </div>
    </div>
  );
};

export default ProductDemo;
